import betslip from '@gin-betting/betslip';
import core, { API, App, GQL, View } from '@gin-betting/core';
import { OrderedSet } from 'immutable';
import { keys } from 'lodash';

import type { IntlMessageArgs } from 'types';
import { getByKey } from 'utils';
import {
    BetslipTimeManager,
    RESTRICTION_BLACK_LIST,
    RESTRICTION_BLACKLIST_BY_BETSLIP_STATUS,
} from './constants';
import messages from './messages';
import type { IBetslipTimeManager } from './types';

const {
    utils: { getBettingOptions, getIdsFromBetslipOddId },
} = core;

const {
    utils: { convertViewMaxStake },
} = betslip;

interface ValidateStake {
    value: string;
    maxValue: number;
}

export const validateStake = ({
    value,
    maxValue,
}: ValidateStake): string | null => {
    let nextValue: string = value;
    const [int = '', fraction = ''] = value.split('.');

    nextValue = nextValue.replace(/,/g, '.');

    if (+nextValue > maxValue) {
        nextValue = convertViewMaxStake(`${maxValue}`);
    }

    if (fraction.length > 2) {
        nextValue = `${+int}.${fraction.slice(0, 2)}`;
    }

    if (+nextValue < 0) {
        return null;
    }

    if (!/^-?\d+\.?\d*$/.test(nextValue) && nextValue !== '') {
        return null;
    }

    return nextValue;
};

export const getRestrictionMessage = (
    restriction: View.Restriction
): IntlMessageArgs => {
    switch (restriction.type) {
        // case (API.RestrictionType.max_bet):

        //     return ({
        //         type: API.RestrictionType.max_bet,
        //         error: `max_bet error maxBet: ${restriction.maxBet}`,
        //     };

        // case API.RestrictionType.bet_delay: {
        //     return getSingleErrors(
        //         restriction,
        //         restrictionId,
        //         `error delay: ${restriction.delay}`
        //     );
        // }
        case API.RestrictionType.max_bet: {
            if (restriction.maxBet === 0) {
                return { descriptor: messages.maxBet };
            }

            return { descriptor: messages.error };
        }

        case API.RestrictionType.bet_type: {
            return {
                descriptor: messages.betType,
            };
        }

        case API.RestrictionType.bet_interval: {
            const { timeToWait } = restriction;

            return {
                descriptor: messages.betInterval,
                values: { timeToWait },
            };
        }

        case API.RestrictionType.selection_value: {
            return {
                descriptor: messages.selectionValue,
            };
        }

        case API.RestrictionType.sport_event_status: {
            return {
                descriptor: messages.sportEventStatus,
            };
        }

        case API.RestrictionType.sport_event_bet_stop: {
            return {
                descriptor: messages.bettingIsSuspended,
            };
        }

        case API.RestrictionType.sport_event_existence: {
            return {
                descriptor: messages.sportEventExistence,
            };
        }

        case API.RestrictionType.market_status: {
            return {
                descriptor: messages.marketStatus,
            };
        }

        case API.RestrictionType.market_existence: {
            return {
                descriptor: messages.marketExistence,
            };
        }

        case API.RestrictionType.market_defective: {
            return {
                descriptor: messages.marketDefective,
            };
        }

        case API.RestrictionType.odd_status: {
            return {
                descriptor: messages.oddStatus,
            };
        }

        case API.RestrictionType.odd_existence: {
            return {
                descriptor: messages.oddExistence,
            };
        }

        default: {
            return {
                descriptor: messages.error,
            };
        }
    }
};

export const getRestrictionBlackList = (status: App.BetslipStatus) =>
    getByKey(RESTRICTION_BLACKLIST_BY_BETSLIP_STATUS, status) ??
    RESTRICTION_BLACK_LIST;

export const getPlaceBetMeta = (): GQL.PlaceBetMeta => {
    const { features } = getBettingOptions();

    if (typeof features !== 'object') return {};

    const featureFlags = Object.entries(features).map(([name, value]) => ({
        name,
        value: value.toString(),
    }));

    return {
        featureFlags,
    };
};

export const getAnalyticOddsInfo = ({
    betslipOddIds,
    withClear,
    betId,
}: {
    betslipOddIds: OrderedSet<string>;
    withClear?: boolean;
    betId?: string;
}): string | null => {
    const payload = betslipOddIds.reduce<IBetslipTimeManager>(
        (acc, betslipOddId) => {
            const time = BetslipTimeManager[betslipOddId];

            if (withClear) {
                delete BetslipTimeManager[betslipOddId];
            }

            if (typeof time === 'undefined') return acc;

            const { oddId } = getIdsFromBetslipOddId(betslipOddId);

            acc[oddId] = time;

            return acc;
        },
        {}
    );

    if (keys(payload).length === 0) return null;

    const result: {
        payload: IBetslipTimeManager;
        timestamp: number;
        betId?: string;
    } = { payload, timestamp: Date.now() };

    if (betId) {
        result.betId = betId;
    }

    return JSON.stringify(result);
};
